// Phase 8 R1 — apply a canned job to a ticket.
// Calls the apply_canned_job RPC (atomic), invalidates caches, and triggers
// the existing board cascade — identical post-add behavior to AddJobDialog.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { getShopDayLabel, getTodayInShopTz } from '@/utils/shopTime';
import { pushRecentCannedJob } from '@/hooks/cannedJobs/useRecentCannedJobs';
import { useAddonSuggestionTrigger } from '@/contexts/AddonSuggestionContext';

interface Vars {
  shopId: string;
  ticketId: string;
  cannedJobId: string;
}

interface ApplyResult {
  parts_inserted: number;
  jobs_inserted: number;
  /** The ticket is a rollover chain row: its minutes were NOT written; re-plan the chain. */
  rollover_chain: boolean;
}

export function useApplyCannedJob() {
  const qc = useQueryClient();
  // CORE-BP item 6. A canned job can carry several labor lines; the question is
  // asked once, after the whole application has settled.
  const { evaluateAfterWorkAdded } = useAddonSuggestionTrigger();

  return useMutation({
    mutationFn: async ({ shopId, ticketId, cannedJobId }: Vars): Promise<ApplyResult> => {
      const { data, error } = await (supabase as any).rpc('apply_canned_job', {
        p_shop_id: shopId,
        p_ticket_id: ticketId,
        p_canned_job_id: cannedJobId,
      });
      if (error) throw error;
      return {
        parts_inserted: Number(data?.parts_inserted ?? 0),
        jobs_inserted: Number(data?.jobs_inserted ?? 0),
        rollover_chain: data?.rollover_chain === true,
      };
    },
    onSuccess: async (result, { shopId, ticketId, cannedJobId }) => {
      // Phase 8 R4 — record in client-side recents (per shop). Pure UX.
      pushRecentCannedJob(shopId, cannedJobId);

      // Invalidate ticket-scoped data
      qc.invalidateQueries({ queryKey: ['parts-lines', shopId, ticketId] });
      qc.invalidateQueries({ queryKey: ['ticket-jobs-for-parts', ticketId] });
      qc.invalidateQueries({ queryKey: ['ticket-jobs'] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['ticket-predicted-minutes', ticketId] });
      qc.invalidateQueries({ queryKey: ['ticket-po-linked-parts-lines'] });

      // ROLLOVER CHAIN: apply_canned_job does not write a chain row's minutes
      // (they are its share of the chain's job ledger). The chain authority
      // re-plans the chain once from the ledger that now includes the new
      // labor lines — and cascades the lanes it touches.
      if (result.jobs_inserted > 0 && result.rollover_chain) {
        try {
          const { recomputeRolloverChainWork } = await import('@/utils/rolloverChain');
          await recomputeRolloverChainWork(ticketId);
        } catch (e) {
          console.warn('[useApplyCannedJob] rollover chain re-plan failed', e);
        }
      }

      // If labor was added, trigger the global board cascade exactly as
      // AddJobDialog does after a labor add.
      if (result.jobs_inserted > 0) {
        try {
          const { data: ticket } = await (supabase as any)
            .from('tickets')
            .select('assigned_tech_id, start_time, scheduled_date, shop_id')
            .eq('id', ticketId)
            .maybeSingle();

          if (ticket) {
            const { data: shopTz } = await (supabase as any)
              .from('shop_profile')
              .select('time_zone')
              .eq('shop_id', ticket.shop_id ?? shopId)
              .maybeSingle();
            const tz = shopTz?.time_zone || 'America/Los_Angeles';
            const dateStr =
              ticket.scheduled_date ??
              (ticket.start_time
                ? getShopDayLabel(ticket.start_time, tz)
                // Chronomancer R-PAST-15: canonical clock-aware helper.
                : getTodayInShopTz(tz));

            const { triggerBoardCascade } = await import('@/utils/cascadeTrigger');
            await triggerBoardCascade(
              ticket.shop_id ?? shopId,
              dateStr,
              ticket.assigned_tech_id || undefined,
            );
          }
        } catch (e) {
          console.warn('[useApplyCannedJob] cascade trigger failed', e);
        }
      }

      const partsLabel =
        result.parts_inserted === 1 ? '1 part' : `${result.parts_inserted} parts`;
      const jobsLabel =
        result.jobs_inserted === 1 ? '1 labor job' : `${result.jobs_inserted} labor jobs`;
      toast({
        title: 'Canned job applied',
        description: `Added ${partsLabel} and ${jobsLabel} to the ticket.`,
      });

      // CORE-BP item 6 — only when LABOR actually joined the ticket. A canned
      // job that added parts alone changed no work, so there is nothing new to
      // evaluate. `jobs_inserted` is the RPC's own report of what it did.
      if (result.jobs_inserted > 0) {
        await evaluateAfterWorkAdded(ticketId);
      }
    },
    onError: (err: any) => {
      toast({
        title: 'Could not apply canned job',
        description: err?.message ?? 'Unknown error',
        variant: 'destructive',
      });
    },
  });
}
